import { cva, type VariantProps } from "class-variance-authority";
import { motion } from "framer-motion";
import { forwardRef, type ElementType, type ButtonHTMLAttributes } from "react";
import SVG from "react-inlinesvg";
import { cn } from "src/lib/cn";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { getLoadingBackground } from "src/sudokit/utils/utils";
import InlineSVG from "react-inlinesvg";
import { useAppStore } from "src/stores/appStore";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center flex-shrink-0 gap-2 min-w-10 font-medium cursor-pointer whitespace-nowrap select-none app-region-no-drag border-none bg-transparent disabled:pointer-events-none disabled:opacity-50 transition-all px-3 font-inter font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-app-primary-cta focus-visible:rounded-[10px]",
  {
    variants: {
      variant: {
        primary: [
          "text-app-cta-text-color",
          "before:content-[''] before:box-border before:opacity-100 before:rounded-[10px] before:border-2 before:border-solid before:border-transparent before:bg-app-primary-cta before:absolute before:h-full before:w-full before:z-[-1] before:transition-[width,height,background-color,border-radius,opacity] before:duration-200 before:ease-in-out",
          "hover:before:bg-app-primary-cta-hover focus-visible:before:bg-app-primary-cta-hover",
          "active:before:rounded-[6px] active:before:h-[calc(100%-6px)] active:before:w-[calc(100%-6px)] active:before:bg-app-primary-cta-active",
        ],
        secondary: [
          "text-app-primary-cta-high-contrast",
          "before:content-[''] before:box-border before:opacity-100 before:rounded-[10px] before:border-2 before:border-solid before:border-app-primary-cta-high-contrast before:bg-transparent before:absolute before:h-full before:w-full before:z-[-1] before:transition-[width,height,background-color,border-radius,opacity] before:duration-200 before:ease-in-out",
          "hover:before:bg-[color-mix(in_srgb,var(--primary-cta-high-contrast)_10%,transparent)] hover:before:border-app-primary-cta-high-contrast focus-visible:before:bg-[color-mix(in_srgb,var(--primary-cta-high-contrast)_10%,transparent)] focus-visible:before:border-app-primary-cta-high-contrast",
          "active:before:rounded-[6px] active:before:h-[calc(100%-6px)] active:before:w-[calc(100%-6px)] active:before:bg-[color-mix(in_srgb,var(--primary-cta-high-contrast)_20%,transparent)] active:before:border-app-primary-cta-high-contrast",
        ],
        tertiary: [
          "text-app-primary-cta-high-contrast",
          "before:content-[''] before:box-border before:opacity-100 before:rounded-[30px] before:border-2 before:border-solid before:border-transparent before:bg-transparent before:absolute before:h-full before:w-full before:z-[-1] before:transition-[width,height,background-color,border-radius,opacity] before:duration-200 before:ease-in-out",
          "hover:before:rounded-[10px] hover:before:bg-[color-mix(in_srgb,var(--primary-cta-high-contrast)_10%,transparent)] focus-visible:before:rounded-[10px] focus-visible:before:bg-[color-mix(in_srgb,var(--primary-cta-high-contrast)_10%,transparent)]",
          "active:before:rounded-[6px] active:before:h-[calc(100%-6px)] active:before:w-[calc(100%-6px)] active:before:bg-[color-mix(in_srgb,var(--primary-cta-high-contrast)_20%,transparent)]",
        ],
        secondaryGrey: [
          "text-app-grey-0",
          "before:content-[''] before:box-border before:opacity-100 before:rounded-[30px] before:border-2 before:border-solid before:border-transparent before:bg-transparent before:absolute before:h-full before:w-full before:z-[-1] before:transition-[width,height,background-color,border-radius,opacity] before:duration-200 before:ease-in-out",
          "hover:before:rounded-[10px] hover:before:bg-[color-mix(in_srgb,var(--grey-0)_10%,transparent)] focus-visible:before:rounded-[10px] focus-visible:before:bg-[color-mix(in_srgb,var(--grey-0)_10%,transparent)]",
          "active:before:rounded-[6px] active:before:h-[calc(100%-6px)] active:before:w-[calc(100%-6px)] active:before:bg-[color-mix(in_srgb,var(--grey-0)_20%,transparent)]",
        ],
        tertiaryGrey: [
          "text-app-grey-1",
          "before:content-[''] before:box-border before:opacity-100 before:rounded-[30px] before:border-2 before:border-solid before:border-transparent before:bg-transparent before:absolute before:h-full before:w-full before:z-[-1] before:transition-[width,height,background-color,border-radius,opacity] before:duration-200 before:ease-in-out",
          "hover:before:rounded-[10px] hover:before:bg-[color-mix(in_srgb,var(--grey-1)_10%,transparent)] focus-visible:before:rounded-[10px] focus-visible:before:bg-[color-mix(in_srgb,var(--grey-1)_10%,transparent)]",
          "active:before:rounded-[6px] active:before:h-[calc(100%-6px)] active:before:w-[calc(100%-6px)] active:before:bg-[color-mix(in_srgb,var(--grey-1)_20%,transparent)]",
        ],
        tertiaryDarkForOverlays: [
          "text-[#fff]",
          "before:content-[''] before:box-border before:opacity-100 before:rounded-[10px] before:border-2 before:border-solid before:border-transparent before:bg-[#383838] before:absolute before:h-full before:w-full before:z-[-1] before:transition-[width,height,background-color,border-radius,opacity] before:duration-200 before:ease-in-out",
          "hover:before:bg-[#484848] focus-visible:before:bg-[#484848]",
          "active:before:rounded-[6px] active:before:h-[calc(100%-6px)] active:before:w-[calc(100%-6px)] active:before:bg-[#585858]",
        ],
        transparent: [
          "text-app-grey-1",
          "before:content-[''] before:box-border before:opacity-100 before:rounded-[30px] before:border-2 before:border-solid before:border-transparent before:bg-transparent before:absolute before:h-full before:w-full before:z-[-1] before:transition-[width,height,background-color,border-radius,opacity] before:duration-200 before:ease-in-out",
          "hover:before:rounded-[10px] hover:before:bg-[color-mix(in_srgb,var(--grey-1)_10%,transparent)] focus-visible:before:rounded-[10px] focus-visible:before:bg-[color-mix(in_srgb,var(--grey-1)_10%,transparent)]",
          "active:before:rounded-[6px] active:before:h-[calc(100%-6px)] active:before:w-[calc(100%-6px)] active:before:bg-[color-mix(in_srgb,var(--grey-1)_20%,transparent)]",
        ],
        error: [
          "text-app-danger-color",
          "before:content-[''] before:box-border before:opacity-100 before:rounded-[30px] before:border-2 before:border-solid before:border-transparent before:bg-transparent before:absolute before:h-full before:w-full before:z-[-1] before:transition-[width,height,background-color,border-radius,opacity] before:duration-200 before:ease-in-out",
          "hover:before:rounded-[10px] hover:before:bg-[color-mix(in_srgb,var(--danger-color)_10%,transparent)] focus-visible:before:rounded-[10px] focus-visible:before:bg-[color-mix(in_srgb,var(--danger-color)_10%,transparent)]",
          "active:before:rounded-[6px] active:before:h-[calc(100%-6px)] active:before:w-[calc(100%-6px)] active:before:bg-[color-mix(in_srgb,var(--danger-color)_20%,transparent)]",
        ],
        danger: [
          "text-app-danger-color",
          "before:content-[''] before:box-border before:opacity-100 before:rounded-[10px] before:border-2 before:border-solid before:border-app-danger-color before:bg-transparent before:absolute before:h-full before:w-full before:z-[-1] before:transition-[width,height,background-color,border-radius,opacity] before:duration-200 before:ease-in-out",
          "hover:before:rounded-[10px] hover:before:bg-[color-mix(in_srgb,var(--danger-color)_10%,transparent)] focus:before:bg-[color-mix(in_srgb,var(--danger-color)_10%,transparent)]",
          "active:before:rounded-[6px] active:before:h-[calc(100%-6px)] active:before:w-[calc(100%-6px)] active:before:bg-[color-mix(in_srgb,var(--danger-color)_20%,transparent)]",
        ],
        dangerFilled: [
          // Darken the danger token so white labels remain legible on the solid fill.
          "text-white",
          "before:content-[''] before:box-border before:opacity-100 before:rounded-[10px] before:border-2 before:border-solid before:border-transparent before:bg-[color-mix(in_srgb,var(--danger-color)_75%,black)] before:absolute before:h-full before:w-full before:z-[-1] before:transition-[width,height,background-color,border-radius,opacity] before:duration-200 before:ease-in-out",
          "hover:before:bg-[color-mix(in_srgb,var(--danger-color)_65%,black)] focus-visible:before:bg-[color-mix(in_srgb,var(--danger-color)_65%,black)]",
          "active:before:rounded-[6px] active:before:h-[calc(100%-6px)] active:before:w-[calc(100%-6px)] active:before:bg-[color-mix(in_srgb,var(--danger-color)_60%,black)]",
        ],
        warning: [
          "text-yellow-600",
          "before:content-[''] before:box-border before:opacity-100 before:rounded-[10px] before:border-2 before:border-solid before:border-yellow-600 before:bg-transparent before:absolute before:h-full before:w-full before:z-[-1] before:transition-[width,height,background-color,border-radius,opacity] before:duration-200 before:ease-in-out",
          "hover:before:rounded-[10px] hover:before:bg-yellow-100 focus:before:bg-yellow-100",
          "active:before:rounded-[6px] active:before:h-[calc(100%-6px)] active:before:w-[calc(100%-6px)] active:before:bg-yellow-200",
        ],
        invertedColor: [
          "text-[var(--surface-inverse-text)]",
          "before:content-[''] before:box-border before:opacity-100 before:rounded-[10px] before:border-2 before:border-solid before:border-transparent before:bg-[var(--surface-inverse)] before:absolute before:h-full before:w-full before:z-[-1] before:transition-[width,height,background-color,border-radius,opacity] before:duration-200 before:ease-in-out",
          "hover:before:bg-[var(--surface-inverse-hover)] focus-visible:before:bg-[var(--surface-inverse-hover)]",
          "active:before:rounded-[6px] active:before:h-[calc(100%-6px)] active:before:w-[calc(100%-6px)] active:before:bg-[var(--surface-inverse-active)]",
        ],
        invertedColorSecondary: [
          "text-[var(--surface-inverse-text)]",
          "before:content-[''] before:box-border before:opacity-100 before:rounded-[10px] before:border-2 before:border-solid before:border-[var(--surface-inverse)] before:bg-transparent before:absolute before:h-full before:w-full before:z-[-1] before:transition-[width,height,background-color,border-radius,opacity] before:duration-200 before:ease-in-out",
          "hover:before:bg-[color-mix(in_srgb,var(--surface-inverse)_15%,transparent)] focus-visible:before:bg-[color-mix(in_srgb,var(--surface-inverse)_15%,transparent)]",
          "active:before:rounded-[6px] active:before:h-[calc(100%-6px)] active:before:w-[calc(100%-6px)] active:before:bg-[color-mix(in_srgb,var(--surface-inverse)_30%,transparent)]",
        ],
      },
      state: {
        default: "",
        loading: "cursor-progress !border-none rounded-[10px] cursor-progress px-3 inline-flex items-center gap-1 font-medium before:!content-none w-fit",
        error: "before:bg-app-danger-color hover:before:bg-app-danger-color-hover active:before:bg-app-danger-color-active",
      },
      size: {
        default: "text-base py-2 h-10 font-medium",
        small: "text-sm py-2 font-medium",
        xsmall: "text-xs py-1 font-medium",
      },
    },
    defaultVariants: {
      variant: "tertiaryGrey",
      size: "default",
      state: "default",
    },
  }
);

export interface SudoButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loadingPercentage?: number;
  iconSize?: number;
  leftIcon?: string;
  rightIcon?: string;
  loadingTransitionDuration?: number;
  as?: ElementType;
  goButton?: boolean;
}

const MotionWithReset = motion(WithReset);

/**
 * Note about gobutton: this will handle styling, but you'll still need to add
 * your own keydown handler to listen for ctrl/meta + enter.
 */
export const SudoButton = forwardRef<HTMLButtonElement, SudoButtonProps>(
  ({ children, className, variant = "tertiaryGrey", size, state, loadingPercentage, iconSize, leftIcon, rightIcon, as, loadingTransitionDuration = 1, goButton = false, ...props }, ref) => {
    const iconSizePx = iconSize ?? (size === "small" ? 16 : 20);
    const isMac = useAppStore((state) => state.isMac);
    const percentage = loadingPercentage !== undefined ? loadingPercentage : 90;

    // Define text color and loading colors based on variant
    const getTextColor = () => {
      if (state === "loading") {
        switch (variant) {
          case "primary":
            return "text-app-cta-text-color"; // White text for primary variants in loading state
          case "tertiary":
            return "text-app-primary-cta-high-contrast";
          case "secondaryGrey":
            return "text-app-grey-0";
          case "tertiaryGrey":
            return "text-app-grey-0";
          case "tertiaryDarkForOverlays":
            return "text-[#383838]";
          case "transparent":
            return "";
          case "invertedColor":
            return "text-[var(--surface-inverse-text)]";
          case "invertedColorSecondary":
            return "text-[var(--surface-inverse-text)]";
          default:
            return "text-app-cta-text-color";
        }
      }
    };

    if (state === "loading") {
      // For primary/secondary buttons, we use the original loading style with grey background
      // For tertiary buttons, we use a transparent background with subtle loading indicator
      return (
        <MotionWithReset
          as={as || "button"}
          ref={ref}
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            width: "fit-content",
          }}
          className={cn(
            buttonVariants({
              variant,
              size,
              state,
            }),
            getTextColor(),
            className
          )}
          initial={{
            background: getLoadingBackground(percentage, true, variant),
          }}
          animate={{
            background: getLoadingBackground(percentage, false, variant),
            transition: {
              duration: loadingTransitionDuration,
              ease: "easeOut",
            },
          }}
          {...props}
          key="loading-button-animation"
        >
          <div className="animate-in zoom-in-50 fade-in-0">
            <SVG
              src={"/assets/loader.svg"}
              width={20}
              height={20}
              className="fill-current [&_path]:fill-current animate-spin"
              aria-hidden="true"
            />
          </div>
          {children}
          {rightIcon && (
            <SVG
              src={rightIcon}
              width={iconSizePx}
              height={iconSizePx}
              className="fill-current [&>path]:fill-current"
              aria-hidden="true"
            />
          )}
        </MotionWithReset>
      );
    }

    return (
      <WithReset
        as={as || "button"}
        ref={ref}
        className={cn(buttonVariants({ variant, size, state }), className)}
        {...props}
      >
        {leftIcon && (
          <SVG
            src={leftIcon}
            width={iconSizePx}
            height={iconSizePx}
            className="fill-current [&>path]:fill-current animate-in fade-in zoom-in"
            aria-hidden="true"
          />
        )}
        {children}
        {rightIcon && (
          <SVG
            src={rightIcon}
            width={iconSizePx}
            height={iconSizePx}
            className="fill-current [&>path]:fill-current"
            aria-hidden="true"
          />
        )}
        {/* GoButton currently only looks good for primary buttons. Will need to be adjusted for other variants if needed. */}
        {goButton && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <InlineSVG
              src={isMac ? "/assets/keyboard-shortcuts/command-key.svg" : "/assets/keyboard-shortcuts/control-key-pc.svg"}
              className="w-6 h-6 border-2 border-white/40 rounded-[5px] [&>path]:fill-white"
              aria-hidden="true"
            />
            <InlineSVG
              src="/assets/keyboard-shortcuts/enter-key.svg"
              className="w-6 h-6 border-2 border-white/40 rounded-[5px] [&>path]:fill-white"
              aria-hidden="true"
            />
          </div>
        )}
      </WithReset>
    );
  }
);

SudoButton.displayName = "SudoButton";
